import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

const PWAInstallContext = createContext(null);

/**
 * Captures the browser PWA install prompt and exposes install actions app-wide.
 *
 * @example
 * <PWAInstallProvider>
 *   <App />
 * </PWAInstallProvider>
 */
export function PWAInstallProvider({ children }) {
  const [prompt, setPrompt] = useState(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  useEffect(() => {
    const handler = (event) => {
      event.preventDefault();
      setPrompt(event);
    };
    const onInstalled = () => setPrompt(null);

    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    if (!prompt) return;
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === "accepted") setPrompt(null);
  }, [prompt]);

  const value = {
    canInstall: Boolean(prompt),
    install,
    bannerDismissed,
    dismissBanner: () => setBannerDismissed(true),
  };

  return (
    <PWAInstallContext.Provider value={value}>
      {children}
    </PWAInstallContext.Provider>
  );
}

/**
 * Read PWA install availability and trigger the native install prompt.
 *
 * @example
 * const { canInstall, install } = usePWAInstall();
 * // canInstall: true
 */
export function usePWAInstall() {
  const context = useContext(PWAInstallContext);
  if (!context) {
    throw new Error("usePWAInstall must be used within PWAInstallProvider");
  }
  return context;
}
